// #686 — runtime probe for the demo-mode sign-in gate.
//
// The gate that decides whether App.tsx shows CD's demo persona chooser
// instead of redirecting to the tenant's real SSO login used to be baked at
// COMPILE time (`VITE_DEMO_MODE`, set from tenant-manifest `tenant.demoMode`
// at compose). The fleet admin toggle writes live D1 `tenants.demo_mode` —
// which ALREADY gates the `/demo/personas` / `/demo/sign-in` backend routes
// per request (`workers/identity/src/routes/demo.ts`,
// `isTenantDemoModeEnabled`) — but that compile-time flag never re-read it,
// so an operator flip only took effect after the next recompose/redeploy.
//
// This hook closes that gap by reusing the SAME already-wired, unauthenticated
// op CD's own Signin persona chooser calls: `getDemoPersonas()`
// (`@v0y4ge/api-client`, GET `/t/:tenantSlug/demo/personas`). It resolves
// (200, even with an empty catalog) when `demo_mode` is on and throws
// `ApiError` with `status === 404` when it's off (see demo.ts's `notFound()` — the SAME 404
// shape as "tenant unknown", by design, so an off-tenant can't be
// distinguished from a nonexistent one). Any other failure (network, 5xx,
// timeout) is ALSO treated as OFF — the existing fail-safe-OFF posture — so a
// probe error never accidentally exposes the demo chooser.
//
// Bounded hang guard (#686 review, lens 2 OFF-path fail-safe): this probe
// gates the ENTIRE sign-in render for every tenant (it fires whenever
// identityBaseUrl is set, not just demo ones), so a connection that hangs
// open (cold worker, stalled D1, a responsive-but-slow identity worker) must
// never leave `loading` stuck at true forever -- that would strand the app on
// blank chrome that never redirects to SSO and never shows the chooser, the
// one OFF-path scenario that would NOT fail safe. The sibling `useWhoami`
// hook guards the identical scenario with an AbortController wired into a
// raw `fetch()` call it owns directly; this hook instead goes through
// `@v0y4ge/api-client`'s `getDemoPersonas()`, which does not (yet) accept a
// cancellation signal, so the equivalent guard here is a plain
// `DEMO_MODE_PROBE_TIMEOUT_MS` timeout that forces the state to resolve
// (fail-safe OFF) once the deadline passes -- WITHOUT waiting any further on
// `getDemoPersonas()`'s promise. A `settled` flag makes whichever settles
// first (the real response or the timeout) win and ignores the other,
// so a slow response that eventually arrives after the timeout can never
// flip the gate back once the render has already moved on.
//
// No `configureApiClient()` call is needed here: `getDemoPersonas()` resolves
// the identity base + tenant from the SAME build-time env vars
// (`VITE_IDENTITY_BASE_URL` / `VITE_IDENTITY_TENANT_SLUG`) App.tsx already
// reads directly, via api-client's own env fallback
// (`ENV_IDENTITY_BASE`/`ENV_TENANT` in `client.ts`) — nothing in this app
// calls `configureApiClient`, so those env fallbacks are exactly what's live.

import { useEffect, useState } from "react";
import { ApiError, getDemoPersonas } from "@v0y4ge/api-client";

// Mirrors useWhoami's WHOAMI_TIMEOUT_MS (same sibling hook, same identity
// worker, same bound) -- see the "Bounded hang guard" note above. Exported so
// the never-resolving-probe test can advance fake timers by exactly this
// amount rather than duplicating the literal.
export const DEMO_MODE_PROBE_TIMEOUT_MS = 8000;

export interface DemoModeState {
  /** True while the runtime probe's first request is in flight. */
  loading: boolean;
  /**
   * Authoritative, live `tenants.demo_mode` value for this tenant — resolved
   * by actually calling the same `/demo/personas` op the persona chooser
   * uses. Never trust anything else (a stale build-time hint, a cached
   * value) once this has resolved.
   */
  enabled: boolean;
  /** True when a successful probe returns no personas; false before a result or on failure. */
  empty: boolean;
}

/**
 * Runtime probe for the demo-mode gate (#686).
 *
 * `identityBaseUrl` absent (e.g. local dev without an identity worker
 * configured) short-circuits to the hint with loading:false and empty:false.
 * There is nothing to probe, so the hint (if any) is all that's available.
 * Otherwise fires `getDemoPersonas()` once on mount (re-fires if
 * `identityBaseUrl`/`tenantSlug` change) and resolves to the live boolean --
 * bounded by `DEMO_MODE_PROBE_TIMEOUT_MS` so a hung request can never leave
 * `loading` stuck at `true` forever (see the "Bounded hang guard" note above).
 */
export function useDemoMode(identityBaseUrl: string, tenantSlug: string, hint = false): DemoModeState {
  const [state, setState] = useState<DemoModeState>({
    loading: Boolean(identityBaseUrl),
    enabled: hint,
    empty: false,
  });

  useEffect(() => {
    if (!identityBaseUrl) {
      setState({ loading: false, enabled: hint, empty: false });
      return;
    }

    let settled = false;
    setState({ loading: true, enabled: hint, empty: false });

    const timeoutId = window.setTimeout(() => {
      if (settled) return;
      settled = true;
      // Fail-safe OFF: the deadline passed with no response at all -- never
      // leave the render stuck on `loading`, and never expose the demo
      // chooser on an inconclusive probe.
      setState({ loading: false, enabled: false, empty: false });
    }, DEMO_MODE_PROBE_TIMEOUT_MS);

    void getDemoPersonas()
      .then((personas) => {
        if (settled) return;
        settled = true;
        window.clearTimeout(timeoutId);
        // A 200 response IS "demo_mode is on" — the route's own gate
        // (isTenantDemoModeEnabled) already decided that; this hook doesn't
        // re-derive enabled from the catalog's shape/length. An empty
        // catalog separately tells the shell to use the real sign-in.
        setState({ loading: false, enabled: true, empty: personas.length === 0 });
      })
      .catch((error) => {
        if (settled) return;
        settled = true;
        window.clearTimeout(timeoutId);
        if (error instanceof ApiError && error.status === 404) {
          setState({ loading: false, enabled: false, empty: false });
          return;
        }
        // Fail-safe OFF: any other non-404 failure (network error, 5xx) must
        // never expose the demo chooser — falls through to the normal
        // tenant-native SSO redirect, same posture as the prior compile-time
        // default.
        setState({ loading: false, enabled: false, empty: false });
      });

    return () => {
      settled = true;
      window.clearTimeout(timeoutId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `hint` is a
    // first-paint seed only; re-running the probe on every hint change would
    // defeat the point (the probe result always wins over the hint).
  }, [identityBaseUrl, tenantSlug]);

  return state;
}
